import { Badge } from "@/components/ui/badge";
import { Star, Mail, Clock, Users, Youtube, MapPin } from "lucide-react";
import { ImageWithFallback } from "@/components/common/ImageWithFallback";
import { Podcast } from "@/types/podcast";
import { stripHtmlTags } from "@/utils/html-utils";
import { formatListenerCount } from "@/utils/format-utils";
import { buildAppleUrl, REGION_NAMES, REGION_FLAGS } from "@/utils/region-utils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { MatchedEpisodes } from "./MatchedEpisodes";

dayjs.extend(relativeTime);

interface PodcastCardProps {
  podcast: Podcast;
  onClick: () => void;
}

export function PodcastCard({ podcast, onClick }: PodcastCardProps) {
  return (
    <button
      onClick={onClick}
      className="w-full max-w-full bg-white rounded-xl border border-border p-2.5 sm:p-4 hover:shadow-md hover:border-primary/20 hover:bg-primary/5 transition-all group text-left flex gap-3 items-start overflow-hidden"
    >
        {/* Podcast Cover */}
        <div className="size-14 sm:size-16 rounded-lg bg-gradient-to-br from-orange-100 to-red-100 flex-shrink-0 overflow-hidden border border-border/50">
          <ImageWithFallback
            src={podcast.thumbnailUrl || podcast.imageUrl}
            alt={podcast.title}
            className="size-full object-cover"
          />
        </div>

        {/* Podcast Info */}
        <div className="flex-1 min-w-0 space-y-0.5 sm:space-y-1 overflow-hidden">
          {/* Title */}
          <h4 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors text-sm pr-1">
            {podcast.title}
          </h4>

          {/* Author */}
          <p className="text-xs text-muted-foreground truncate pr-1">
            by {podcast.author || 'Unknown Host'}
          </p>

          {/* Description */}
          <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed break-words">
            {stripHtmlTags(podcast.description)}
          </p>


          {/* Badges Row */}
          <div className="flex items-center gap-2 flex-wrap pt-1.5">
            {/* Rating Badge */}
            {podcast.appleRating && (
                (podcast.externalLinks?.applePodcasts || podcast.itunesId) ? (
              <a
                href={podcast.externalLinks?.applePodcasts || (podcast.itunesId ? buildAppleUrl(podcast.itunesId, podcast.primaryMarket) : '#')}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors"
                title={`View on Apple Podcasts${podcast.primaryMarket ? ` (${REGION_NAMES[podcast.primaryMarket] || podcast.primaryMarket.toUpperCase()})` : ''}`}
              >
                <Star className="size-3 text-amber-500 fill-amber-500" />
                <span className="text-xs font-semibold text-amber-700">
                  {podcast.appleRating.toFixed(1)} {podcast.appleRatingCount ? `(${podcast.appleRatingCount})` : ''}
                </span>
              </a>
                ) : (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200">
                    <Star className="size-3 text-amber-500 fill-amber-500" />
                    <span className="text-xs font-semibold text-amber-700">
                    {podcast.appleRating.toFixed(1)} {podcast.appleRatingCount ? `(${podcast.appleRatingCount})` : ''}
                    </span>
                </div>
                )
            )}

            {/* Primary Market Badge */}
            {podcast.primaryMarket && (
              <Badge
                variant="outline"
                className="text-purple-600 bg-purple-50 border-purple-200 text-[10px] px-2 h-6 font-normal gap-1"
              >
                {REGION_FLAGS[podcast.primaryMarket] || <MapPin className="size-3" />} {podcast.primaryMarket.toUpperCase()}
              </Badge>
            )}

            {/* Language/Category Tags */}
            {podcast.language && (
              <Badge
                variant="secondary"
                className="bg-slate-100 text-slate-600 border-0 hover:bg-primary/10 hover:text-primary transition-colors text-[10px] px-2 h-6"
              >
                {podcast.language.toUpperCase()}
              </Badge>
            )}
             {/* Episode Count */}
             {podcast.episodeCount && (
              <Badge
                variant="outline"
                className="text-slate-500 border-slate-200 text-[10px] px-2 h-6 font-normal"
              >
                {podcast.episodeCount} eps
              </Badge>
            )}
             {/* Listener Estimate */}
             {podcast.estimatedListeners != null && podcast.estimatedListeners > 0 && (
              <Badge
                variant="outline"
                className="text-indigo-600 bg-indigo-50 border-indigo-200 text-[10px] px-2 h-6 font-normal gap-1"
              >
                <Users className="size-3" /> {formatListenerCount(podcast.estimatedListeners)}
              </Badge>
            )}

            {/* Youtube Subscribers */}
            {podcast.youtubeSubscriberCount && (
                <a 
                    href={podcast.youtubeUrl || (podcast.youtubeHandle ? `https://youtube.com/${podcast.youtubeHandle}` : '#')}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                >
                    <Badge
                        variant="outline"
                        className="text-red-600 bg-red-50 border-red-200 text-[10px] px-2 h-6 font-normal gap-1 hover:bg-red-100 cursor-pointer transition-colors"
                    >
                        <Youtube className="size-3" /> {podcast.youtubeSubscriberCount}
                    </Badge>
                </a>
            )}

             {/* Has Email Badge */}
             {podcast.hasEmail ? (
              <Badge
                variant="outline"
                className="text-emerald-600 bg-emerald-50 border-emerald-200 text-[10px] px-2 h-6 font-normal gap-1"
              >
                <Mail className="size-3" /> Email
              </Badge>
            ) : (
               <Badge
                variant="outline"
                className="text-slate-400 bg-slate-50 border-slate-200 text-[10px] px-2 h-6 font-normal gap-1"
              >
                <Mail className="size-3 opacity-50" /> No Email
              </Badge>
            )}
             {/* Last Updated */}
             {podcast.lastEpisodeDate && (
              <span className="text-[10px] text-slate-400 flex items-center gap-1">
                 <Clock className="size-3" /> {dayjs(podcast.lastEpisodeDate).fromNow()}
              </span>
            )}
          </div>

          {/* Matched Episodes Section */}
          <MatchedEpisodes episodes={podcast.matchedEpisodes} />
        </div>
    </button>
  );
}
